import React, { useState, useRef, useEffect } from 'react';
import { parseColors, renderColoredText } from '../utils/colorParser';
import './ServerCard.css';

const ServerCard = ({ server, onTagClick }) => {
  const serverName = server.vars?.sv_projectName || 'Unknown Server';
  const serverDescription = server.vars?.sv_projectDesc || '';
  const serverTags = server.vars?.tags ? server.vars.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [];
  const currentPlayers = server.clients || 0;
  const maxPlayers = server.svMaxclients || server.vars?.sv_maxClients || 0;
  const playerPercentage = maxPlayers > 0 ? (currentPlayers / maxPlayers) * 100 : 0;
  const serverImageUrl = `https://vmp.724548.ir.cdn.ir/saturn/icon/${server?.id}-${server?.iconVersion}.png`;
  const coloredServerName = parseColors(serverName);
  const upvotePower = server.upvotePower || 0;

  const handleServerClick = () => {
    window.history.pushState({}, '', `/${server.id}`);
    // Force a location change event that the App component can detect
    window.dispatchEvent(new CustomEvent('routechange'));
  };

  const getPlayerStatusColor = () => {
    if (playerPercentage >= 90) return '#ef4444';
    if (playerPercentage >= 70) return '#f59e0b';
    if (playerPercentage >= 30) return '#10b981';
    return '#6b7280';
  };

  const getBoostTier = (boosts) => {
    if (boosts >= 500) return 'ultra-premium';
    if (boosts >= 200) return 'premium';
    if (boosts >= 101) return 'flashy';
    if (boosts >= 51) return 'bold';
    if (boosts >= 21) return 'prominent';
    return 'modest';
  };

  const boostTier = getBoostTier(upvotePower);
  const [showAllTags, setShowAllTags] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="server-card" onClick={handleServerClick} style={{ cursor: 'pointer' }}>
      <div className="server-card-header">
        <div className="server-image-container" ref={imageRef}>
          {isVisible ? (
            !imageError ? (
              <img 
                src={serverImageUrl} 
                alt={`${serverName} icon`}
                className={`server-image ${imageLoaded ? 'loaded' : 'loading'}`}
                width="48"
                height="48"
                style={{ width: '48px', height: '48px' }}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="server-image-placeholder" style={{ 
                width: '48px', 
                height: '48px', 
                backgroundColor: '#374151', 
                borderRadius: '8px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '20px',
                color: '#9ca3af'
              }}>
                📊
              </div>
            )
          ) : (
            <div className="server-image-placeholder">
              <div className="image-loading-spinner"></div>
            </div>
          )}
        </div>
        <div className="server-details">
          <div className="server-name-row">
            <div className="server-name-section">
              <h3 className="server-name" style={{ margin: 0, display: 'inline' }}>
                {coloredServerName.length > 0 ? renderColoredText(coloredServerName) : serverName}
                <span 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigator.clipboard.writeText(server.id);
                    setIdCopied(true);
                    setTimeout(() => setIdCopied(false), 1500);
                  }}
                  title="Click to copy server ID"
                  style={{ 
                    cursor: 'pointer', 
                    marginLeft: '4px',
                    fontSize: '11px',
                    color: idCopied ? '#10b981' : '#6b7280',
                    fontWeight: '400',
                    transition: 'color 0.2s ease',
                    verticalAlign: 'middle',
                    position: 'relative',
                    top: '-1px'
                  }}
                >
                  {idCopied ? '✓ Copied!' : `#${server.id}`}
                </span>
              </h3>
              {serverTags.length > 0 && (
                <div className="server-tags-inline" style={{ marginBottom: '8px' }}>
                  {(showAllTags ? serverTags : serverTags.slice(0, 10)).map((tag, index) => (
                    <button 
                      key={index} 
                      className={`server-tag server-tag-${index % 6} server-tag-clickable`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onTagClick && onTagClick(tag);
                      }}
                      title={`Filter by tag: ${tag}`}
                      style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        margin: '1px',
                        lineHeight: '1.2'
                      }}
                    >
                      {tag}
                    </button>
                  ))}
                  {serverTags.length > 10 && (
                    <button 
                      className="server-tag server-tag-more"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAllTags(!showAllTags);
                      }}
                      style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        margin: '1px',
                        lineHeight: '1.2'
                      }}
                    >
                      {showAllTags ? 'Show less' : `+${serverTags.length - 10} more`}
                    </button>
                  )}
                </div>
              )}
              {serverDescription && (
                <p className="server-description">{serverDescription}</p>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
              <a 
                href={`https://dev.vmp.ir/boost/${server.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`boost-indicator boost-${boostTier} boost-clickable`}
                title={upvotePower === 0 ? "Click to boost this server!" : `${upvotePower} boosts - Click to boost more!`}
                onClick={(e) => e.stopPropagation()}
                style={{
                  transform: 'scale(2) !important',
                  transformOrigin: 'center !important',
                  fontSize: '20px !important',
                  padding: '16px 20px !important'
                }}
              >
                <span className="boost-icon">🚀</span>
                <span className="boost-count">{upvotePower}</span>
                {upvotePower === 0 && <span className="boost-text">Boost</span>}
              </a>
              <div className="player-count" style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                <span className="current-players">{currentPlayers}</span>
                <span className="separator">/</span>
                <span className="max-players">{maxPlayers}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default ServerCard;